/**
 * Front-Desk Arrivals Console (F9) API client.
 *
 * Thin wrappers over the backend staff router (`backend/app/routers/staff.py`,
 * prefix `/staff`, all RBAC `facility:checkin`). The frontend owns ZERO business
 * logic: the backend resolves display names, owns payment_status, and owns the
 * confirm side-effect. This client only builds the exact wire bytes.
 *
 * Backend contract — PINNED against backend/app/routers/staff.py +
 * backend/common/models.py:
 *   GET  /staff/facilities/{facility_id}/stats/today      -> TodayStatsResponse
 *   GET  /staff/facilities/{facility_id}/checkins/pending  -> PendingCheckinsResponse{entries}
 *   GET  /staff/checkins/{checkin_id}                      -> CheckinDetailResponse
 *   POST /staff/checkins/{checkin_id}/confirm              <- ConfirmCheckinRequest|None (204)
 *   GET  /staff/facilities/{facility_id}/checkins/recent   -> RecentCheckinsResponse{entries}
 *   GET  /facilities/{facility_id}/players/search?q=...     -> FacilityPlayerSearchResult[]
 *
 * ADR-005 follow-up: the pending + recent endpoints return `entries` as an
 * untyped `List[dict]` on the backend (staff.py builds raw dicts, not Pydantic
 * response items for the pending feed), so these request/response shapes are
 * HAND-WRITTEN mirrors rather than codegen aliases from
 * `@bw/shared/types/generated`. Tracked for follow-up: type the backend entries
 * as real Pydantic models so these can move to the generated source of truth.
 */
import { httpClient } from "@bw/shared/api";
import type { Schema } from "@bw/shared/types/generated";

// ---------------------------------------------------------------------------
// Types — hand-written mirrors of the staff.py contract (see ADR-005 note above)
// ---------------------------------------------------------------------------

export type TodayStats = Schema["TodayStatsResponse"];

export interface PendingArrival {
  checkin_id: string;
  user_id: string;
  // RESOLVED by the backend (falls back to user_id server-side). Pending rows
  // carry NO payment_status — only the DETAIL endpoint does.
  display_name: string;
  booking_type: "booking" | "walkin";
  court_name?: string | null;
  start_time?: string | null;
  checked_in_at: string | null;
}

export interface CheckinDetail {
  checkin_id: string;
  user_id: string;
  display_name: string;
  facility_id: string;
  court_name?: string | null;
  booking_id?: string | null;
  membership_status?: string | null;
  // Required, truthful — never fabricate "paid".
  payment_status: string;
  checked_in_at: string | null;
}

export interface RecentCheckin {
  checkin_id: string;
  user_id: string;
  display_name?: string | null;
  // CheckinResponse carries facility_id; the recent feed is already
  // facility-scoped so the console never needs it, but it is part of the wire
  // shape (kept so the typed payload is honest).
  facility_id?: string;
  court_id?: string | null;
  status: string;
  checked_in_at: string | null;
}

export type PlayerSearchResult = Schema["FacilityPlayerSearchResult"];

// Confirm body — both fields optional. We never INVENT a payment_method the
// operator did not choose.
export interface ConfirmCheckinBody {
  payment_method?: string | null;
  staff_id?: string | null;
}

// Envelope shapes for the `entries`-wrapped list endpoints.
interface PendingCheckinsResponse {
  entries: PendingArrival[];
}
interface RecentCheckinsResponse {
  entries: RecentCheckin[];
}

// ---------------------------------------------------------------------------
// Clients
// ---------------------------------------------------------------------------

export function getTodayStats(facilityId: string): Promise<TodayStats> {
  return httpClient.get<TodayStats>(
    `/staff/facilities/${encodeURIComponent(facilityId)}/stats/today`,
  );
}

export async function listPendingArrivals(
  facilityId: string,
): Promise<PendingArrival[]> {
  const res = await httpClient.get<PendingCheckinsResponse>(
    `/staff/facilities/${encodeURIComponent(facilityId)}/checkins/pending`,
  );
  return res.entries;
}

export function getCheckinDetail(checkinId: string): Promise<CheckinDetail> {
  return httpClient.get<CheckinDetail>(
    `/staff/checkins/${encodeURIComponent(checkinId)}`,
  );
}

export function confirmCheckin(
  checkinId: string,
  body?: ConfirmCheckinBody,
): Promise<void> {
  return httpClient.post<void>(
    `/staff/checkins/${encodeURIComponent(checkinId)}/confirm`,
    body,
  );
}

export async function listRecentCheckins(
  facilityId: string,
): Promise<RecentCheckin[]> {
  const res = await httpClient.get<RecentCheckinsResponse>(
    `/staff/facilities/${encodeURIComponent(facilityId)}/checkins/recent`,
  );
  return res.entries;
}

export function searchPlayers(
  facilityId: string,
  q: string,
): Promise<PlayerSearchResult[]> {
  // q is required (backend min_length 2). URLSearchParams percent-encodes the
  // value so `&`, spaces, `%` cannot break the query string.
  const params = new URLSearchParams({ q });
  return httpClient.get<PlayerSearchResult[]>(
    `/facilities/${encodeURIComponent(facilityId)}/players/search?${params.toString()}`,
  );
}
